import { useScrollAreaContext } from '@/components/Layout/ScrollAreaContext'
import { useRouterState } from '@tanstack/react-router'
import { RefObject, useEffect, useRef, useState } from 'react'

export function isElementContentOverflow(element: HTMLElement): boolean {
	const isOverflow =
		// 其实这个地方只需要判断 scrollHeight 和 clientHeight 就可以了，当 scrollHeight 大于 clientHeight 时，就说明元素的内容已经溢出了。
		// 但有且仅当在 Safari 缩放比率为 115% 和 125% 时，scrollHeight 和 clientHeight 的差值会出现 1px 的误差，所以这里加上了 1px 的误差。其他的浏览器任何缩放比率都没有这个问题。
		element.scrollHeight - element.clientHeight > 1 || element.scrollWidth - element.clientWidth > 1
	return isOverflow
}

export function useScrollToTop() {
	const { scrollContainerRef } = useScrollAreaContext()

	return function scrollToTop() {
		if (!scrollContainerRef.current) return
		const isScrolled = scrollContainerRef.current?.scrollTop !== 0

		if (isScrolled) {
			scrollContainerRef.current?.scrollTo({
				top: 0,
				behavior: 'smooth',
			})
		}
	}
}

export function useIsMobileShellHidden(threshold = 8) {
	const { scrollContainerRef } = useScrollAreaContext()
	const [isHidden, setIsHidden] = useState(false)
	const previousScrollTopRef = useRef(0)

	useEffect(() => {
		const scrollContainer = scrollContainerRef.current

		if (!scrollContainer) return

		previousScrollTopRef.current = scrollContainer.scrollTop

		const handleScroll = () => {
			const currentScrollTop = scrollContainer.scrollTop
			const scrollDelta = currentScrollTop - previousScrollTopRef.current
			const isScrollable = scrollContainer.scrollHeight > scrollContainer.clientHeight + 1

			if (!isScrollable || currentScrollTop <= 0) {
				setIsHidden(false)
				previousScrollTopRef.current = currentScrollTop
				return
			}

			if (Math.abs(scrollDelta) < threshold) return

			setIsHidden(scrollDelta > 0)
			previousScrollTopRef.current = currentScrollTop
		}

		scrollContainer.addEventListener('scroll', handleScroll, { passive: true })

		return () => {
			scrollContainer.removeEventListener('scroll', handleScroll)
		}
	}, [scrollContainerRef, threshold])

	return isHidden
}

export const useIsTabBarHidden = useIsMobileShellHidden

export const SCROLL_POSITION_CACHE: Record<string, number> = {}

export function useScrollPostionRestoration(scrollContainerRef: RefObject<HTMLElement | null>) {
	const {
		isLoading,
		isTransitioning,
		location: { pathname },
	} = useRouterState()

	useEffect(() => {
		if (isLoading || isTransitioning) {
			return
		}

		const top = SCROLL_POSITION_CACHE[pathname] ?? 0
		scrollContainerRef.current?.scrollTo({ top, behavior: 'instant' })
	}, [isLoading, isTransitioning, pathname])

	return {
		cacheScrollPosition: (y: number) => {
			SCROLL_POSITION_CACHE[pathname] = y
		},
	}
}
